import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Button,
	Checkbox,
	CheckboxGroup,
	Flex,
	FormControl,
	FormHelperText,
	FormLabel,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	rem,
	Select,
	Stack,
	Text,
	TextProps,
	useToast,
} from '@bonusly/design-system';
import { useAnalytics, useCurrentCompany, useFeatureFlag } from '@bonusly/who-am-i';

import { useDeleteAnnouncement, useInvalidateAnnouncementsQueries } from '../hooks/use-announcements';
import { useGetIncentives } from '../hooks/use-incentives';
import { Announcement, Incentive } from '../types/announcements';
import { AnnouncementTrackingEvent, Variant } from '../types/events';

export interface ViewAnnouncementModalProps {
	announcement: Announcement;
	isOpen: boolean;
	onClose: VoidFunction;
}

const ViewAnnouncementModal: FC<ViewAnnouncementModalProps> = ({ announcement, isOpen, onClose }) => {
	const { t } = useTranslation('announcements');
	const company = useCurrentCompany();
	const msteamsMessageEnabled = useFeatureFlag(Variant.MsTeams2025) && company.chat_integrations.has_ms_teams_integration;
	const slackMessageEnabled = company.chat_integrations.has_slack_integration;
	const { track } = useAnalytics();
	const toast = useToast();
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const { mutate: deleteAnnouncement, isLoading: isDeleting } = useDeleteAnnouncement();
	const invalidateQueries = useInvalidateAnnouncementsQueries();

	const { data: companyIncentives } = useGetIncentives({ recipients: [] });
	const companyHasIncentives = companyIncentives && companyIncentives.length > 0;

	useEffect(() => {
		// track only once when the modal is opened
		if (!isOpen) return;
		track(AnnouncementTrackingEvent.ViewAnnouncementModalViewed);
	}, [isOpen, track]);

	const handleDelete = () => {
		deleteAnnouncement(
			{ id: announcement.id },
			{
				onError: () => {
					setErrorMessage(t('modal.delete_error_message'));
				},
				onSuccess: () => {
					onClose();
					invalidateQueries();
					toast({
						title: t('modal.delete_success_message'),
						status: 'success',
						position: 'bottom-right',
					});
				},
			}
		);
	};

	const reasonTokens = announcement.reason_tokens.map((token) => token.token);

	return (
		<Modal
			autoFocus={false}
			isOpen={isOpen}
			onClose={() => {
				track(AnnouncementTrackingEvent.ViewAnnouncementModalClosed);
				onClose();
			}}
			scrollBehavior="inside"
			size="2xl"
		>
			<ModalOverlay />
			<ModalContent as="form">
				<ModalHeader borderBottomWidth={rem(1)} borderColor="gray.100" borderStyle="solid" color="gray.900">
					{t('modal.view_header')}
				</ModalHeader>
				<ModalCloseButton data-testid="close-button" size="lg" borderRadius="full" />

				<ModalBody p={5}>
					<Stack spacing={5}>
						<FormControl isDisabled>
							<StyledFormLabel>{t('modal.recipient_label')}</StyledFormLabel>
							<Flex
								alignItems="center"
								borderColor="border.input"
								borderRadius="md"
								borderWidth="1px"
								cursor="text"
								data-testid="recipient-input"
								h={10}
								px={4}
								py={0}
							>
								{reasonTokens.length > 0 ? (
									reasonTokens.map((token) => (
										<StyledReasonToken data-testid="reason-token" key={token}>
											{token}
										</StyledReasonToken>
									))
								) : (
									<StyledReasonToken data-testid="reason-token">{`@${t('modal.everyone')}`}</StyledReasonToken>
								)}
							</Flex>
							<FormHelperText>{t('modal.recipient_helper')}</FormHelperText>
						</FormControl>
						<FormControl isDisabled>
							<StyledFormLabel>{t('modal.title_label')}</StyledFormLabel>
							<Input
								data-testid="title-input"
								borderColor="border.input"
								_disabled={{ color: 'text.default' }}
								_hover={{ borderColor: 'border.input' }}
								cursor="text"
								fontSize={50}
								value={announcement.title}
							/>
							<FormHelperText>
								{t('modal.title_helper', {
									count: announcement.title.length,
								})}
							</FormHelperText>
						</FormControl>
						<FormControl isDisabled>
							<StyledFormLabel>{t('modal.description_label')}</StyledFormLabel>
							<Box borderColor="border.input" borderRadius="md" borderWidth="1px" cursor="text" px={4} py={1.5}>
								<Text
									dangerouslySetInnerHTML={{ __html: announcement.description }}
									fontSize={50}
									fontWeight="normal"
									lineHeight={7}
									__css={{
										a: {
											color: 'text.interactive',
											'&:hover': {
												textDecoration: 'underline',
											},
										},
										blockquote: {
											borderLeftWidth: '4px',
											borderLeftStyle: 'solid',
											borderLeftColor: 'gray.200',
											marginLeft: 0,
											paddingLeft: 4,
										},
										li: {
											marginLeft: 7,
										},
										pre: {
											backgroundColor: 'grayAlpha.100',
											padding: 2,
											fontSize: 50,
										},
									}}
								/>
							</Box>
							<FormHelperText>
								{t('modal.description_helper', {
									count: announcement.description.length,
								})}
							</FormHelperText>
						</FormControl>
						<FormControl isDisabled>
							<StyledFormLabel>{t('modal.start_date_label')}</StyledFormLabel>
							<Input
								data-testid="start-date-input"
								type="date"
								borderColor="border.input"
								_hover={{ borderColor: 'border.input' }}
								_disabled={{ color: 'text.default' }}
								fontSize={50}
								value={announcement.start_date.split('T')[0]}
							/>
							<FormHelperText>{t('modal.start_date_helper')}</FormHelperText>
						</FormControl>
						<FormControl isDisabled>
							<StyledFormLabel>{t('modal.end_date_label')}</StyledFormLabel>
							<Input
								data-testid="end-date-input"
								type="date"
								borderColor="border.input"
								_hover={{ borderColor: 'border.input' }}
								_disabled={{ color: 'text.default' }}
								fontSize={50}
								value={announcement.end_date.split('T')[0]}
							/>
							<FormHelperText>{t('modal.end_date_helper')}</FormHelperText>
						</FormControl>
						<FormControl isDisabled>
							<StyledFormLabel>{t('modal.incentive_label')} </StyledFormLabel>
							<Select
								placeholder={t('modal.no_incentive')}
								_disabled={{
									borderColor: 'border.input',
									color: 'text.default',
									opacity: 1,
								}}
								data-testid="incentive-input"
								fontSize={50}
								value={announcement.claimable_award_id || ''}
							>
								{companyHasIncentives &&
									companyIncentives.map((incentive: Incentive) => {
										return (
											<option key={incentive.id} value={incentive.id}>
												{incentive.name}
											</option>
										);
									})}
							</Select>
						</FormControl>
						<Box>
							<StyledFormLabel>{t('modal.image_upload_label')}</StyledFormLabel>
							<Text data-testid="announcement-image" fontSize={50} color="text.default">
								{announcement.image?.filename || t('modal.no_image')}
							</Text>
						</Box>
						<Box>
							<FormLabel fontSize={50}>{t('modal.notification_label')}</FormLabel>
							<FormControl>
								<CheckboxGroup>
									<Checkbox data-testid="email-notification-checkbox" isChecked={announcement.email_notification} isDisabled>
										<Text display="inline-block" pt={1.5} fontSize={50} fontWeight="normal" lineHeight={5}>
											{t('modal.email_notification_label')}
										</Text>
									</Checkbox>
									<Checkbox
										mt={2}
										data-testid="slack-notification-checkbox"
										display={slackMessageEnabled ? 'block' : 'none'}
										isChecked={announcement.send_slack_message}
										isDisabled
									>
										<Text display="inline-block" fontSize={50} fontWeight="normal" lineHeight={5}>
											{t('modal.send_slack_message_label')}
										</Text>
									</Checkbox>
									<Checkbox
										mt={2}
										data-testid="ms-teams-notification-checkbox"
										display={msteamsMessageEnabled ? 'block' : 'none'}
										isChecked={announcement.send_ms_teams_message}
										isDisabled
									>
										<Text display="inline-block" fontSize={50} fontWeight="normal" lineHeight={5}>
											{t('modal.send_ms_teams_message_label')}
										</Text>
									</Checkbox>
								</CheckboxGroup>
							</FormControl>
						</Box>
					</Stack>
				</ModalBody>

				<ModalFooter flexDirection="column" borderTopColor="border.default" borderTopStyle="solid" borderTopWidth="1px">
					<Flex justify="space-between" w="100%">
						<Button colorScheme="secondary" data-testid="delete-button" isLoading={isDeleting} onClick={handleDelete}>
							{t('modal.delete_button')}
						</Button>
						<Button colorScheme="primary" data-testid="save-button" isDisabled>
							{t('modal.save_button')}
						</Button>
					</Flex>
					{errorMessage && (
						<Text color="error.text" fontSize={50} mt={4} textAlign="right" w="100%">
							{errorMessage}
						</Text>
					)}
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

ViewAnnouncementModal.displayName = 'View Announcement Modal';

export { ViewAnnouncementModal };

const StyledFormLabel = ({ ...props }) => {
	return <FormLabel fontSize={50} _disabled={{ color: 'text.default' }} {...props} />;
};

const StyledReasonToken = (props: TextProps) => {
	return <Text borderRadius="100px" bgColor="info.bg" color="info.text" px={1.5} py={0.5} fontSize={50} mr={1} {...props} />;
};
